import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { AccessorialCode, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface AccessorialCodeStore {
  accessorialCodes: Ref<Loadable<AccessorialCode[]>>;
  loadRequestableAccessorialCodes: (opts?: PerformLoadOptions) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useAccessorialCodeStore = defineStore(
  'accessorialCode',
  (): AccessorialCodeStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const toast = useToast();

    const accessorialCodes: Ref<Loadable<AccessorialCode[]>> = ref({
      loading: false,
    });

    const loadRequestableAccessorialCodes = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const numAccessorialCodes = accessorialCodes.value.data?.length ?? 0;
      const isLoading = accessorialCodes.value.loading;
      const requiresLoad = numAccessorialCodes === 0 && !isLoading;

      if (options.bypassCache || requiresLoad) {
        return performLoad();
      }
    };

    const performLoad = async () => {
      try {
        accessorialCodes.value.loading = true;
        accessorialCodes.value.data =
          await api.accessorialCode.findAllRequestable();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        accessorialCodes.value.error = e;
        console.error('Error loading accessorial codes', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('referenceData.accessorials', 2),
          }),
          error: e,
        });
      } finally {
        accessorialCodes.value.loading = false;
      }
    };

    watch(selectedDomainId, () => {
      accessorialCodes.value.data = [];
    });

    return {
      accessorialCodes,
      loadRequestableAccessorialCodes,
    };
  },
);

export const useAccessorialCodeStore = (): AccessorialCodeStore => {
  const store = _useAccessorialCodeStore();
  const { accessorialCodes } = storeToRefs(store);
  const { loadRequestableAccessorialCodes } = store;

  return {
    accessorialCodes,
    loadRequestableAccessorialCodes,
  };
};
